import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ASSESSMENT_URL, DEMO_URL } from './shared';
// Suite lockups reuse the site's existing brand SVGs rather than duplicating
// the equivalent PNGs from the v0 project.
import rentDirectLogo from '../../assets/images/Black_RentDirect_Vertical.svg';
import rentIdLogo from '../../assets/images/rentID_Vertical.svg';
import rentShieldLogo from '../../assets/images/Black_RentShield_Verticle.svg';
import rentAiLogo from '../../assets/images/Black_RentAI_Vertical.svg';
import rentSuccessLogo from '../../assets/images/Black_RentSuccess_Vertical.svg';
import rentAcademyLogo from '../../assets/images/Black_RentAcademy_Vertical.svg';

// Index order is shared: paragraph keyword i <-> logo i.
const SUITE = [
  { name: 'RentDirect', src: rentDirectLogo, href: '#product-rentdirect', keyword: 'rent' },
  { name: 'rentID', src: rentIdLogo, href: '#product-rentid', keyword: 'verify' },
  { name: 'RentShield', src: rentShieldLogo, href: '#product-rentshield', keyword: 'insure' },
  { name: 'RentAI', src: rentAiLogo, href: '#product-rentai', keyword: 'automate' },
  { name: 'RentSuccess', src: rentSuccessLogo, href: '#product-rentsuccess', keyword: 'resolve' },
  { name: 'RentAcademy', src: rentAcademyLogo, href: '#product-rentacademy', keyword: 'grow' },
];

// Springy, liquid easing — slight overshoot so items settle like a fluid surface.
const LIQUID_EASE = 'cubic-bezier(0.22, 1.4, 0.36, 1)';

/** Description with interactive keywords linked to the suite logos below. */
const SuiteCallout = () => {
  const [hovered, setHovered] = useState(null);

  const clear = () => setHovered(null);

  // An interactive keyword that mirrors the hover/anchor of its matching logo.
  const Keyword = ({ index }) => {
    const item = SUITE[index];
    const active = hovered === index;
    return (
      <a
        href={item.href}
        aria-label={`Jump to ${item.name}`}
        onMouseEnter={() => setHovered(index)}
        onMouseLeave={clear}
        onFocus={() => setHovered(index)}
        onBlur={clear}
        className={`mpa-suite-kw${active ? ' is-active' : ''}`}
      >
        {item.keyword}
      </a>
    );
  };

  return (
    <>
      <p className="mpa-suite-copy">
        Rentid empowers fleet operators with everything needed to <Keyword index={0} />,{' '}
        <Keyword index={1} />, <Keyword index={2} />, <Keyword index={3} />, <Keyword index={4} />,
        and <Keyword index={5} /> direct rentals. Focus on your business, customers, and personal
        goals, not fighting marketplace rules.
      </p>

      {/* Rentid product suite logos — each links to its product section below. */}
      <ul className="mpa-suite-logos">
        {SUITE.map((l, i) => {
          const distance = hovered === null ? null : Math.abs(i - hovered);

          // Liquid displacement: the hovered logo swells; neighbors ripple
          // outward, with the effect decaying over distance.
          let scale = 1;
          let translateX = 0;
          let translateY = 0;

          if (distance !== null) {
            if (distance === 0) {
              scale = 1.45;
              translateY = -10;
            } else if (distance === 1) {
              scale = 1.12;
              translateX = (i < hovered ? -1 : 1) * 8;
              translateY = -3;
            } else if (distance === 2) {
              scale = 1.03;
              translateX = (i < hovered ? -1 : 1) * 3;
            }
          }

          return (
            <li key={l.src}>
              <a
                href={l.href}
                aria-label={`Jump to ${l.name}`}
                className="mpa-suite-logo"
                style={{
                  transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
                  transition: `transform 450ms ${LIQUID_EASE}`,
                }}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={clear}
                onFocus={() => setHovered(i)}
                onBlur={clear}
              >
                <img src={l.src} alt={`${l.name} logo`} />
              </a>
            </li>
          );
        })}
      </ul>
    </>
  );
};

/** Callout — eyebrow + big headline (left), copy + suite logos (right). */
const CalloutSection = () => (
  <section className="mpa-container mpa-callout">
    <div className="mpa-callout-grid">
      {/* Left — eyebrow link + headline + CTAs */}
      <div className="mpa-callout-left">
        <Link to="https://app.rentid.io/sign-up" className="mpa-callout-link">
          Build fast. Start renting today. All in one place. <span aria-hidden="true">&rarr;</span>
        </Link>
        <h3 className="mpa-callout-title">Everything to do anything rental.</h3>

        <div className="mpa-callout-ctas">
          <a
            href={DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mpa-callout-btn mpa-callout-btn--green"
          >
            Book Demo
          </a>
          <Link to={ASSESSMENT_URL} className="mpa-callout-btn mpa-callout-btn--muted">
            Speak with an Expert
          </Link>
        </div>
      </div>

      {/* Right — description + suite logos */}
      <div className="mpa-callout-right">
        <SuiteCallout />
      </div>
    </div>
  </section>
);

export default CalloutSection;
